'use client';

import Card from '@/components/Card/Card';
import type { FormField } from '@/components/Form/helper/formHelper';
import { Button } from '@/components/button';
import LoadingBackdrop from '@/components/loadingBackdrop/LoadingBackdrop';
import type { SummaryProps } from '@/components/pdf/FormSummary';
import { renderFormSummary } from '@/components/pdf/actions/renderPdfAction';
import { openPdf } from '@/components/pdf/utils';
import { getFieldValue, hideFormField } from '@/helper/utils';
import { getTokens } from '@/helper/vtwutils';
import { useFlashStore } from '@/providers/FlashStoreProvider';
import { useSettings } from '@/providers/SettingsProvider';
import type { ComponentFormSummaryStoryblok } from '@/types/component-types-sb';
import { useTranslation } from 'react-i18next';

import classes from './FormSummary.module.scss';

type FormSummaryProps = {
  blok: ComponentFormSummaryStoryblok;
};

const FormSummary: React.FC<FormSummaryProps> = ({ blok }) => {
  const { t } = useTranslation();
  const formFields: FormField[] = useFlashStore((s) => s.formData);
  const settings = useSettings();

  if (!formFields) return <LoadingBackdrop loading={true} />;

  // TODO maybe redirect to other page
  // if (formFields.length == 0) return null;

  const render = async () => {
    const props: SummaryProps = {
      translations: {
        title: t('Zusammenfassung'),
      },
      formFields: formFields,
      settings: settings,
      tokens: getTokens(),
    };
    const base64string = await renderFormSummary(props);
    return base64string;
  };

  return (
    <div className={classes.FormSummary}>
      <div>
        <h1>{blok.title}</h1>
        <span>{blok.description}</span>
      </div>
      {formFields.length > 0 && (
        <>
          <div className={classes.SummaryHeader}>
            <h3>{t('Zusammenfassung')}</h3>
            <div>
              <Button variant="text" onClick={() => openPdf(render)} data-testid="open-pdf">
                Drucken
              </Button>
            </div>
          </div>

          <Card>
            <div className="grid-auto-fill-250">
              {formFields.map((field) => {
                if (hideFormField(field)) return null;
                return (
                  <div key={field.fieldId}>
                    {field.fieldLabel && (
                      <p className={classes.fieldLabel}>
                        <strong>{field.fieldLabel}</strong>
                      </p>
                    )}
                    <span>{getFieldValue(field)}</span>
                  </div>
                );
              })}
            </div>
          </Card>
        </>
      )}
    </div>
  );
};

export default FormSummary;
